import { useCallback, useState } from 'react'
import { useAppDispatch } from '@/features/hooks'
import {
  deleteMealSpaceReservationDetail,
  saveMealSpaceReservation,
} from '@/features/restaurant-manager/restaurant-manager-thunks'
import { useErrorHandler } from '@/hooks/use-error-handler'
import type { RestaurantMealSpaceSelection } from '@/models/restaurant-manager'
import type { DraftRow } from './use-restaurant-meal-space-draft'
import { isSameSelection } from './use-restaurant-meal-space-draft'

type Options = {
  reservationId: string
  getRow: (rowId: string) => DraftRow | null
  getCurrentRows: () => DraftRow[]
  applySelection: (rowId: string, selection: RestaurantMealSpaceSelection) => DraftRow[]
  replaceDraftRows: (nextRows: DraftRow[]) => void
  removeRowById: (rowId: string) => void
  setOpenDropdownRowId: (rowId: string | null) => void
  setCandidateResponse: (value: null) => void
}

export const useRestaurantMealSpaceSave = ({
  reservationId,
  getRow,
  getCurrentRows,
  applySelection,
  replaceDraftRows,
  removeRowById,
  setOpenDropdownRowId,
  setCandidateResponse,
}: Options) => {
  const dispatch = useAppDispatch()
  const { errorHandler } = useErrorHandler()
  const [isSaving, setIsSaving] = useState(false)

  const saveRowSelection = useCallback(
    async (rowId: string, selection: RestaurantMealSpaceSelection) => {
      if (isSaving) {
        return
      }

      const currentRow = getRow(rowId)
      if (!currentRow) {
        return
      }

      if (currentRow.currentSelection && isSameSelection(currentRow.currentSelection, selection)) {
        setOpenDropdownRowId(null)
        return
      }

      const currentRows = getCurrentRows()
      const optimisticRows = applySelection(rowId, selection)
      const shouldKeepSelectionOnFailure = currentRow.spaceUsageDetailId == null

      try {
        setIsSaving(true)

        await dispatch(
          saveMealSpaceReservation({
            reservationId,
            spaceUsageDetailId: currentRow.spaceUsageDetailId,
            selection,
          }),
        ).unwrap()

        if (currentRow.spaceUsageDetailId == null) {
          // 新規追加行は保存成功時に draft から取り除く（再取得後の一覧側で表示される）
          replaceDraftRows(optimisticRows.filter(row => row.localRowId !== rowId))
        }

        setOpenDropdownRowId(null)
        setCandidateResponse(null)
      } catch (error) {
        errorHandler(error)
        replaceDraftRows(shouldKeepSelectionOnFailure ? optimisticRows : currentRows)
      } finally {
        setIsSaving(false)
      }
    },
    [
      applySelection,
      dispatch,
      errorHandler,
      getCurrentRows,
      getRow,
      isSaving,
      replaceDraftRows,
      reservationId,
      setCandidateResponse,
      setOpenDropdownRowId,
    ],
  )

  const deleteRow = useCallback(
    async (row: DraftRow, confirmMessage: string) => {
      if (isSaving) {
        return
      }

      if (row.spaceUsageDetailId == null) {
        removeRowById(row.localRowId)
        setOpenDropdownRowId(null)
        return
      }

      if (!window.confirm(confirmMessage)) {
        return
      }

      try {
        setIsSaving(true)
        await dispatch(deleteMealSpaceReservationDetail({ spaceUsageDetailId: row.spaceUsageDetailId })).unwrap()
        setOpenDropdownRowId(null)
        setCandidateResponse(null)
      } catch (error) {
        errorHandler(error)
      } finally {
        setIsSaving(false)
      }
    },
    [dispatch, errorHandler, isSaving, removeRowById, setCandidateResponse, setOpenDropdownRowId],
  )

  return { isSaving, saveRowSelection, deleteRow }
}
